import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { map, Subscription, switchMap, tap } from 'rxjs';
import { APP_ROUTES } from '../../app.routes';

const REQUESTED_ROUTE = 'requestedRouteBeforeAuth';
const ERROR_CYCLE_ONCE = 'errorCycleOneAttempt';

interface JWT {
  authzData: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  oidcSecurityService = inject(OidcSecurityService);
  private router = inject(Router);

  destroyRef = inject(DestroyRef);
  isAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
    takeUntilDestroyed(this.destroyRef),
    map((res) => res.isAuthenticated),
  );

  private checkAuthSub?: Subscription;

  login() {
    this.checkAuthSub = this.oidcSecurityService
      .checkAuth()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(
          (res) => {
            const publicRoutes = ['/logout'];
            const currentPath = window.location.pathname;
            if (res.isAuthenticated || publicRoutes.includes(currentPath)) {
              this.handleValidAuth();
            } else {
              this.handleError(res);
            }
          },
          () => {
            this.handleError({} as LoginResponse);
          },
        ),
      )
      .subscribe();
  }

  handleValidAuth() {
    localStorage.removeItem(ERROR_CYCLE_ONCE);
    const requestedRoute = localStorage.getItem(REQUESTED_ROUTE);

    if (requestedRoute) {
      localStorage.removeItem(REQUESTED_ROUTE);
      this.router.navigateByUrl(requestedRoute);
    }
  }

  handleError(res: LoginResponse) {
    // different errors need different handling to avoid infinite loops
    const hasErrorFlag = res.errorMessage;
    const errorCycleOnce = localStorage.getItem(ERROR_CYCLE_ONCE);
    const isAuthError =
      res.errorMessage &&
      res.errorMessage.startsWith(
        'Error: authorizedCallback, token(s) validation failed',
      );
    const isUnauthenticatedButNoError = !res.isAuthenticated && !hasErrorFlag;

    // --- Case 1: Not authenticated, no real error ---
    if (isUnauthenticatedButNoError) {
      this.goToIdentityLoginPage();
      return;
    }

    // --- Case 2: First real authentication error ---
    if (isAuthError) {
      localStorage.setItem(ERROR_CYCLE_ONCE, 'true');
      window.location.href = 'https://help.nessy.com/';
      return;
    }

    // --- Case 3: first error we give another chance otherwise error page ---
    if (hasErrorFlag && errorCycleOnce) {
      localStorage.removeItem(ERROR_CYCLE_ONCE);
      console.error('Authentication error occurred again. Showing error page.');
      this.router.navigateByUrl(APP_ROUTES.ERROR);
      return;
    } else {
      // --- Case 4: subsequent error + plus fallback ---
      localStorage.setItem(ERROR_CYCLE_ONCE, 'true');
      this.goToIdentityLoginPage();
      return;
    }
  }

  logout() {
    this.oidcSecurityService
      .preloadAuthWellKnownDocument()
      .pipe(switchMap(() => this.oidcSecurityService.logoffAndRevokeTokens()))
      .subscribe((res) => {
        console.log('logged out of UD', res);
        this.destroy();
      });
  }

  private destroy() {
    if (sessionStorage.getItem('appSessionState'))
      sessionStorage.removeItem('appSessionState');
    if (localStorage.getItem('dashboard')) localStorage.removeItem('dashboard');
    if (localStorage.getItem('0-nessy.understandingdyslexia.client'))
      localStorage.removeItem('0-nessy.understandingdyslexia.client');
    if (this.checkAuthSub) this.checkAuthSub.unsubscribe();
  }

  goToIdentityLoginPage() {
    // store landing url in local storage
    localStorage.setItem(REQUESTED_ROUTE, window.location.pathname);

    // pass from_student_login query string param through to ID if it exists
    const customParams: Record<string, string> = {};

    const urlParams = new URLSearchParams(document.location.search);
    const fromStudentLogin = urlParams.get('from_student_login');

    if (fromStudentLogin) {
      customParams['from_student_login'] = fromStudentLogin;
    }

    this.oidcSecurityService.authorize(undefined, { customParams });
  }

  bearerToken() {
    return this.oidcSecurityService.getAccessToken().pipe(
      map((token) => {
        const jwt = localStorage.getItem(
          '0-nessy.understandingdyslexia.client',
        );

        const jwtParse: JWT = jwt ? JSON.parse(jwt) : { authzData: '' };

        return token ? token : jwtParse.authzData;
      }),
    );
  }

  refreshToken() {
    this.oidcSecurityService.forceRefreshSession().subscribe({
      next: () => {
        this.router.navigateByUrl(APP_ROUTES.HOME);
      },
      error: () => {
        this.router.navigateByUrl(APP_ROUTES.ERROR);
      },
    });
  }
}
